"use client";

import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import type { Contact } from "@/lib/types/database";
import { useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";

// Generic components
import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useDealHooks } from "@/features/deals/hooks/use-deals-hooks";

// Deal-specific components
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Deal } from "@/lib/types/database/deals";

const ENHANCED_DEALS_CONFIG = getEntityTableConfig<Deal>("deals");

// Types
import type { EntityFormProps } from "@/components/shared/UniversalEntityPage/types";

interface ContactDealsProps {
  client: Contact;
  className?: string;
}

export const ContactDeals: React.FC<ContactDealsProps> = ({ client, className = "" }) => {
  // Call hooks at the top level first
  const dealHooks = useDealHooks();
  const globalModalStack = useGlobalModalStack();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Entity form props for template support
  const entityFormProps: EntityFormProps<Deal> = useMemo(() => ({}), []);

  // Create a custom config that filters by company
  const clientDealConfig = useMemo(
    () => ({
      ...ENHANCED_DEALS_CONFIG,
      // Add company-specific filters
      defaultFilters: {
        company_id: client.company_id,
      },
      // Override display name for context
      displayName: `Deals for ${client.company_name || client.name}`,
    }),
    [client.company_id, client.company_name, client.name]
  );

  // Custom header component with stats and create button
  const ClientDealsHeader = () => {
    return (
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">
            Deals for {client.company_name || client.name}
          </h3>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              Contact: {client.name}
            </Badge>
            {client.company_name && (
              <Badge variant="outline" className="text-xs">
                Company: {client.company_name}
              </Badge>
            )}
          </div>
        </div>

        {/* Create Deal Button */}
        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              globalModalStack.openModal(MODAL_IDS.deals.create);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4" />
            Create Deal
          </Button>
        </div>
      </div>
    );
  };

  // If contact has no company, show a message
  if (!client.company_id) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-card-foreground mb-2">No Company</h3>
            <p className="text-gray-600 dark:text-gray-400">
              This contact is not associated with a company. Deals are linked to companies.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <ClientDealsHeader />

      {/* Use the generic UniversalEntityPage with company-specific configuration */}
      <UniversalEntityPage
        entityName="deals"
        config={clientDealConfig}
        hooks={dealHooks}
        filterPreset="all"
        title={`Deals for ${client.company_name || client.name}`}
        description={`Manage deals for ${client.company_name || client.name}`}
        storageKey={`client-${client.id}-deals-column-preferences`}
        // Pass company filter to the hooks
        customFilters={{
          company_id: client.company_id,
        }}
        entityFormProps={entityFormProps}
        // Hide the default header since we have a custom one
        showHeader={false}
      />
    </div>
  );
};

export { ContactDeals as ClientDeals };
